// ============================================================
// RISK VISION ENGINE — Fase 9.11
// Avalia risco visual: PPE, áreas bloqueadas, crowding, paradas
// Totalmente heurístico — pronto para upgrade com ML
// ============================================================
import type { VisionEvent, PPEViolation, CameraDevice } from '@/types/vision'
import type { VisionInsight } from '@/types/vision'
import { createVisionEvent, publishVisionEvent } from '@/core/vision/visionEventBus'

export interface VisionRiskContext {
  obraId:          string
  cameras:         CameraDevice[]
  recentEvents:    VisionEvent[]
  ppeViolations:   PPEViolation[]
  offlineCameras:  number
  totalCameras:    number
}

export interface VisionRiskScore {
  score:      number   // 0-100 (0=ok, 100=critical)
  level:      'low' | 'moderate' | 'high' | 'critical'
  components: { label: string; score: number; weight: number }[]
  insights:   VisionInsight[]
}

// ─── Weights ──────────────────────────────────────────────────
const WEIGHTS = {
  ppeCompliance:    0.35,
  motionAnomalies:  0.25,
  cameraHealth:     0.20,
  congestion:       0.20,
}

// ─── Main ─────────────────────────────────────────────────────
export function evaluateVisionRisk(ctx: VisionRiskContext): VisionRiskScore {
  const insights: VisionInsight[] = []
  const now = Date.now()
  const H1 = 3_600_000   // 1 hour window

  // ── 1. PPE compliance score ──────────────────────────────────
  const recentPPE = ctx.ppeViolations.filter(v => new Date(v.ts).getTime() > now - H1)
  const criticalPPE = recentPPE.filter(v => v.is_critical).length
  const ppeScore = Math.min(100, criticalPPE * 20 + recentPPE.length * 8)

  if (criticalPPE > 0) {
    insights.push(buildInsight(ctx.obraId, 'ppe_risk', 'critical',
      `🦺 ${criticalPPE} violação(ões) crítica(s) de EPI`,
      `${criticalPPE} trabalhador(es) sem EPI obrigatório em área crítica na última hora`,
      'Paralisar área e conduzir DDS emergencial sobre uso de EPI',
      0.9,
    ))
  } else if (recentPPE.length > 2) {
    insights.push(buildInsight(ctx.obraId, 'ppe_risk', 'warning',
      `🦺 Múltiplas não-conformidades de EPI`,
      `${recentPPE.length} ocorrências de EPI nos últimos 60 min`,
      'Reforçar supervisão no uso de EPIs',
      0.8,
    ))
  }

  // ── 2. Motion anomalies ──────────────────────────────────────
  const inactivity = ctx.recentEvents.filter(e =>
    e.tipo === 'inactivity_detected' && !e.is_resolved
  )
  const congestions = ctx.recentEvents.filter(e => e.tipo === 'congestion_detected')
  const motionScore = Math.min(100, inactivity.length * 15 + congestions.length * 25)

  if (inactivity.length > 0) {
    insights.push(buildInsight(ctx.obraId, 'inactivity_risk', 'warning',
      `⏸ ${inactivity.length} área(s) sem atividade operacional`,
      inactivity.map(e => e.title).join('; '),
      'Verificar status das equipes nestas áreas',
      0.75,
    ))
  }

  if (congestions.length > 0) {
    insights.push(buildInsight(ctx.obraId, 'congestion_risk', 'warning',
      `🔴 Congestionamento detectado em ${congestions.length} câmera(s)`,
      'Alta concentração de operários detectada visualmente',
      'Redistribuir equipes para evitar riscos de segurança',
      0.72,
    ))
  }

  // ── 3. Camera health ─────────────────────────────────────────
  const offlineRate = ctx.totalCameras > 0 ? ctx.offlineCameras / ctx.totalCameras : 0
  const cameraScore = Math.min(100, offlineRate * 100)

  if (offlineRate > 0.5) {
    insights.push(buildInsight(ctx.obraId, 'camera_health', 'critical',
      `📷 ${ctx.offlineCameras} câmera(s) offline — cobertura comprometida`,
      `${Math.round(offlineRate * 100)}% das câmeras sem sinal`,
      'Verificar conectividade e alimentação das câmeras offline',
      0.95,
    ))
  } else if (offlineRate > 0.2) {
    insights.push(buildInsight(ctx.obraId, 'camera_health', 'warning',
      `📷 ${ctx.offlineCameras} câmera(s) offline`,
      'Cobertura visual parcialmente comprometida',
      'Reiniciar câmeras offline',
      0.85,
    ))
  }

  // ── 4. Congestion score (already counted above) ──────────────
  const congestionScore = Math.min(100, congestions.length * 30)

  // ── Weighted total ────────────────────────────────────────────
  const total = Math.round(
    ppeScore       * WEIGHTS.ppeCompliance +
    motionScore    * WEIGHTS.motionAnomalies +
    cameraScore    * WEIGHTS.cameraHealth +
    congestionScore * WEIGHTS.congestion,
  )

  const level =
    total >= 70 ? 'critical' :
    total >= 45 ? 'high' :
    total >= 20 ? 'moderate' : 'low'

  return {
    score: total,
    level,
    components: [
      { label: 'EPI',         score: ppeScore,        weight: WEIGHTS.ppeCompliance },
      { label: 'Movimento',   score: motionScore,     weight: WEIGHTS.motionAnomalies },
      { label: 'Câmeras',     score: cameraScore,     weight: WEIGHTS.cameraHealth },
      { label: 'Concentração', score: congestionScore, weight: WEIGHTS.congestion },
    ],
    insights,
  }
}

// ─── Factory ─────────────────────────────────────────────────
function buildInsight(
  obraId:         string,
  tipo:           string,
  severity:       VisionInsight['severity'],
  title:          string,
  description:    string,
  recommendation: string,
  confidence:     number,
): VisionInsight {
  return {
    id:             `vi_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    obra_id:        obraId,
    tipo,
    severity,
    title,
    description,
    recommendation,
    confidence,
    evidence:       [],
    expires_at:     new Date(Date.now() + 3_600_000).toISOString(),
    created_at:     new Date().toISOString(),
  }
}
